/**
 * Pre-flight dashboard questionnaire (Move 5 — Step 1).
 *
 * 5-question modal that maps deterministically to a scaffold request.
 * Replaces the agent-guesses-archetype path with a UI gate so the build
 * gets the right archetype + theme + params on the first try.
 *
 * No external modal library — plain React with inline styles to match
 * the rest of the taskpane.
 */

import React, { useState } from 'react';
import {
  WizardAnswers,
  Industry,
  Purpose,
  Granularity,
  PURPOSE_LABELS,
  INDUSTRY_LABELS,
  GRANULARITY_LABELS,
  DEFAULT_ANSWERS,
} from '../services/questionnaireToScaffold';

interface Props {
  open: boolean;
  busy?: boolean;
  workbookFileName?: string | null;
  onCancel: () => void;
  onSubmit: (answers: WizardAnswers) => void;
}

export function DashboardWizard({ open, busy, workbookFileName, onCancel, onSubmit }: Props) {
  const [answers, setAnswers] = useState<WizardAnswers>(DEFAULT_ANSWERS);

  if (!open) return null;

  const set = <K extends keyof WizardAnswers>(key: K, value: WizardAnswers[K]) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
  };

  return (
    <div style={s.backdrop}>
      <div style={s.modal} role="dialog" aria-modal="true" aria-label="Build dashboard">
        <div style={s.header}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Build dashboard</div>
          <button style={s.closeBtn} onClick={onCancel} disabled={busy} title="Cancel">✕</button>
        </div>

        <div style={s.body}>
          {workbookFileName && (
            <div style={s.fileLine}>
              <span style={{ color: '#0a6e3a', fontWeight: 600 }}>✓</span> {workbookFileName}
            </div>
          )}
          <p style={s.intro}>
            Six quick questions. Your answers pick the right dashboard layout, theme,
            KPI count, commentary mode and audit panel — no guessing, no extra
            back-and-forth with the AI.
          </p>

          <Question num={1} label="What industry?">
            <RadioGrid<Industry>
              name="industry"
              value={answers.industry}
              options={INDUSTRY_LABELS}
              onChange={v => set('industry', v)}
            />
          </Question>

          <Question num={2} label="What is this dashboard for?">
            <div style={s.purposeList}>
              {PURPOSE_LABELS.map(opt => (
                <label
                  key={opt.value}
                  style={{
                    ...s.purposeRow,
                    borderColor: answers.purpose === opt.value ? '#0078d4' : '#e5e5e5',
                    background: answers.purpose === opt.value ? '#eef4fb' : '#fff',
                  }}
                >
                  <input
                    type="radio"
                    name="purpose"
                    value={opt.value}
                    checked={answers.purpose === opt.value}
                    onChange={() => set('purpose', opt.value)}
                    style={{ marginRight: 6 }}
                  />
                  <span style={{ flex: 1 }}>
                    <span style={{ fontWeight: 600 }}>{opt.label}</span>
                    <span style={s.purposeHint}>{opt.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </Question>

          <Question num={3} label="Data granularity?">
            <RadioGrid<Granularity>
              name="granularity"
              value={answers.granularity}
              options={GRANULARITY_LABELS}
              onChange={v => set('granularity', v)}
            />
          </Question>

          <Question num={4} label="How many KPI tiles?">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="range"
                min={4}
                max={8}
                step={1}
                value={answers.kpiCount}
                onChange={e => set('kpiCount', parseInt(e.target.value, 10))}
                style={{ flex: 1 }}
              />
              <div style={s.kpiPill}>{answers.kpiCount}</div>
            </div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
              Most board packs land between 4 and 6. More than 8 fights the layout.
            </div>
          </Question>

          <Question num={5} label="Generate executive commentary?">
            <label style={s.commentaryRow}>
              <input
                type="checkbox"
                checked={answers.includeCommentary}
                onChange={e => set('includeCommentary', e.target.checked)}
                style={{ marginRight: 8 }}
              />
              <span>
                <span style={{ fontWeight: 600 }}>Yes, draft a 2–3 paragraph narrative</span>
                <span style={s.purposeHint}>
                  Headline → causes → forward look, written by Sonnet against the actual
                  numbers from your workbook. Adds ~5 seconds to the build.
                </span>
              </span>
            </label>
          </Question>

          <Question num={6} label="Add an on-sheet audit trail?">
            <label style={s.commentaryRow}>
              <input
                type="checkbox"
                checked={answers.includeAuditPanel}
                onChange={e => set('includeAuditPanel', e.target.checked)}
                style={{ marginRight: 8 }}
              />
              <span>
                <span style={{ fontWeight: 600 }}>Yes, render KPI traceability table</span>
                <span style={s.purposeHint}>
                  Small table at the bottom: each KPI tile, its cell address, the source
                  row, and a live FORMULATEXT() of the underlying formula. Useful when
                  presenting to auditors or directors who want to see the working.
                </span>
              </span>
            </label>
          </Question>
        </div>

        <div style={s.footer}>
          <button style={s.secondaryBtn} onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            style={{ ...s.primaryBtn, opacity: busy ? 0.6 : 1 }}
            onClick={() => onSubmit(answers)}
            disabled={busy}
          >
            {busy ? 'Building…' : 'Build dashboard'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface RadioOpt<T extends string> { value: T; label: string }

function RadioGrid<T extends string>({
  name, value, options, onChange,
}: {
  name: string;
  value: T;
  options: ReadonlyArray<RadioOpt<T>>;
  onChange: (v: T) => void;
}) {
  return (
    <div style={s.radioGrid}>
      {options.map(opt => (
        <label
          key={opt.value}
          style={{
            ...s.radioPill,
            borderColor: value === opt.value ? '#0078d4' : '#cbd5e0',
            background: value === opt.value ? '#eef4fb' : '#fafafa',
            color: value === opt.value ? '#0953a0' : '#1f3864',
            fontWeight: value === opt.value ? 600 : 500,
          }}
        >
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            style={{ display: 'none' }}
          />
          {opt.label}
        </label>
      ))}
    </div>
  );
}

function Question({ num, label, children }: { num: number; label: string; children: React.ReactNode }) {
  return (
    <div style={s.question}>
      <div style={s.questionLabel}>
        <span style={s.questionNum}>{num}</span> {label}
      </div>
      <div>{children}</div>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(20, 30, 50, 0.5)',
    display: 'flex', alignItems: 'stretch', justifyContent: 'stretch',
    zIndex: 1000,
  },
  modal: {
    flex: 1,
    background: '#fff',
    display: 'flex', flexDirection: 'column',
    fontSize: 13,
    overflow: 'hidden',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 14px', borderBottom: '1px solid #e5e5e5',
    background: '#1f3864', color: '#fff',
  },
  closeBtn: {
    background: 'transparent', border: 'none', color: '#fff',
    cursor: 'pointer', fontSize: 16, padding: 2,
  },
  body: {
    flex: 1, overflowY: 'auto', padding: '12px 14px',
  },
  fileLine: {
    fontSize: 11, color: '#5a5a5a', marginBottom: 8,
    background: '#f0f9f1', border: '1px solid #cce8d6',
    borderRadius: 4, padding: '4px 8px',
  },
  intro: {
    margin: '0 0 12px 0', color: '#444', fontSize: 12, lineHeight: 1.4,
  },
  question: {
    marginBottom: 14, paddingBottom: 12, borderBottom: '1px solid #f0f0f0',
  },
  questionLabel: {
    fontWeight: 600, fontSize: 13, marginBottom: 8, color: '#1f1f1f',
    display: 'flex', alignItems: 'center', gap: 6,
  },
  questionNum: {
    display: 'inline-block', width: 20, height: 20,
    background: '#0078d4', color: '#fff', borderRadius: '50%',
    textAlign: 'center', lineHeight: '20px', fontSize: 11, fontWeight: 700,
  },
  radioGrid: {
    display: 'flex', flexWrap: 'wrap', gap: 6,
  },
  radioPill: {
    fontSize: 12, padding: '6px 10px',
    border: '1px solid #cbd5e0', borderRadius: 12,
    cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
  },
  purposeList: {
    display: 'flex', flexDirection: 'column', gap: 4,
  },
  purposeRow: {
    display: 'flex', alignItems: 'flex-start',
    padding: '8px 10px', border: '1px solid #e5e5e5',
    borderRadius: 6, cursor: 'pointer', fontSize: 12,
  },
  purposeHint: {
    display: 'block', fontSize: 11, color: '#666', marginTop: 2, fontWeight: 400,
  },
  commentaryRow: {
    display: 'flex', alignItems: 'flex-start',
    padding: '8px 10px', border: '1px solid #e5e5e5',
    borderRadius: 6, cursor: 'pointer', fontSize: 12,
  },
  kpiPill: {
    minWidth: 28, textAlign: 'center',
    background: '#0078d4', color: '#fff',
    padding: '4px 10px', borderRadius: 14,
    fontWeight: 700, fontSize: 13,
  },
  footer: {
    display: 'flex', justifyContent: 'flex-end', gap: 6,
    padding: '10px 14px', borderTop: '1px solid #e5e5e5',
    background: '#fafafa',
  },
  primaryBtn: {
    padding: '8px 14px', fontSize: 13, fontWeight: 600,
    color: '#fff', background: '#0078d4', border: 'none',
    borderRadius: 4, cursor: 'pointer',
  },
  secondaryBtn: {
    padding: '8px 14px', fontSize: 13, fontWeight: 500,
    color: '#1f1f1f', background: '#fff', border: '1px solid #ccc',
    borderRadius: 4, cursor: 'pointer',
  },
};
